import React, { useState } from 'react';
import moment, { Moment } from 'moment';
import { Field, Form, Formik } from 'formik';
import { FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import LrButton from '../../LrButton/components/Button';
import { makeStyles } from '@mui/styles';
import { DatePicker } from '@mui/x-date-pickers';
import { Spacing } from '../../Layout';

export interface DateRange {
    startDate?: Moment;
    endDate?: Moment;
    qualifier?: string;
    option?: DatePickerSettings;
}

export interface DatePickerSettings {
    label: string;
    hideStartDate?: boolean;
    hideEndDate?: boolean;
    removeEmptyColumns?: boolean;
    qualifiers?: Array<string | { code: string; displayValue: string }>;
    qualifierLabel?: string;
    [index: string]: any;
    addQualifierToFilename?: boolean;
}

interface DateRangePickerFormProps {
    onSubmit: (values: DateRange) => void;
    submitText: string;
    labelText?: string;
    defaultStart?: Moment;
    defaultEnd?: Moment;
    options?: DatePickerSettings[];
}

const DateRangePickerForm: React.FunctionComponent<DateRangePickerFormProps> = (props) => {
    const { defaultStart, defaultEnd, onSubmit, labelText, submitText, options } = { ...props };
    const initialStart = defaultStart || moment().subtract(1, 'months').startOf('month');
    const initialEnd = defaultEnd || moment().subtract(1, 'months').endOf('month');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const initialOption = options && options.length ? 0 : undefined;
    const initialValues = {
        startDate: initialStart,
        endDate: initialEnd,
        optionIndex: initialOption,
        qualifierIdx: 0,
    };

    const classes = makeStyles({
        select: {
            '&.MuiSelect-select': {
                padding: '6px 24px 7px 5px',
            },
        },
        formControl: {
            minWidth: 120,
            marginTop: '0px',
        },
    })();

    const getOption = (idx?: number) => {
        if (idx !== undefined && options) {
            return options[idx];
        }
        return undefined;
    };

    return (
        <>
            {labelText && (
                <>
                    <Typography variant={'h4'} sx={{ textDecoration: 'underline' }}>
                        {labelText}
                    </Typography>
                    <Spacing height={'8px'} />
                </>
            )}
            <Formik
                initialValues={initialValues}
                onSubmit={async (values) => {
                    const dateRange: DateRange = { startDate: values.startDate, endDate: values.endDate };
                    dateRange.option = getOption(values.optionIndex);
                    const qualifier =
                        dateRange.option?.qualifiers && dateRange.option?.qualifiers?.length > values.qualifierIdx
                            ? dateRange.option?.qualifiers[values.qualifierIdx]
                            : undefined;

                    if (typeof qualifier === 'string') {
                        dateRange.qualifier = qualifier;
                    } else {
                        dateRange.qualifier = qualifier?.code;
                    }
                    setIsSubmitting(true);
                    onSubmit && (await onSubmit(dateRange));
                    setIsSubmitting(false);
                }}
            >
                {({ values }) => (
                    <Form>
                        <Grid container spacing={1} sx={{ display: 'flex' }}>
                            {!getOption(values.optionIndex)?.hideStartDate && (
                                <Grid>
                                    <Field name="startDate">
                                        {({ field, form, meta }: any) => (
                                            <DatePicker
                                                label="Start Date"
                                                format="MM/DD/YYYY"
                                                value={field.value}
                                                slotProps={{
                                                    textField: {
                                                        helperText: meta.error,
                                                    },
                                                }}
                                                onError={(err) => {
                                                    // handle as a side effect
                                                    if (err !== meta.error) {
                                                        form.setFieldError(field.name, err);
                                                    }
                                                }}
                                                // if you are using custom validation schema you probably want to pass `true` as third argument
                                                onChange={(date) => form.setFieldValue(field.name, date, false)}
                                            />
                                        )}
                                    </Field>
                                </Grid>
                            )}
                            {!getOption(values.optionIndex)?.hideEndDate && (
                                <Grid>
                                    <Field name="endDate">
                                        {({ field, form, meta }: any) => (
                                            <DatePicker
                                                label="End Date"
                                                format="MM/DD/YYYY"
                                                value={field.value}
                                                slotProps={{
                                                    textField: {
                                                        helperText: meta.error,
                                                    },
                                                }}
                                                onError={(err) => {
                                                    // handle as a side effect
                                                    if (err !== meta.error) {
                                                        form.setFieldError(field.name, err);
                                                    }
                                                }}
                                                // if you are using custom validation schema you probably want to pass `true` as third argument
                                                onChange={(date) => form.setFieldValue(field.name, date, false)}
                                            />
                                        )}
                                    </Field>
                                </Grid>
                            )}
                            {!!getOption(values.optionIndex)?.qualifiers?.length && (
                                <Grid>
                                    <Field name="qualifierIdx">
                                        {({ field, form, meta }: any) => (
                                            <FormControl
                                                variant="standard"
                                                margin={'normal'}
                                                className={classes.formControl}
                                            >
                                                <InputLabel id={'option-select-label'}>
                                                    {getOption(form.values.optionIndex)?.qualifierLabel}
                                                </InputLabel>
                                                <Select
                                                    classes={{ select: classes.select }}
                                                    labelId={'qualifier-select-label'}
                                                    variant={'standard'}
                                                    id={'qualifier-select'}
                                                    onChange={(val) =>
                                                        form.setFieldValue(field.name, val.target.value, false)
                                                    }
                                                    value={field.value}
                                                    name={field.name}
                                                    error={meta.error}
                                                >
                                                    {((getOption(form.values.optionIndex)?.qualifiers ||
                                                        []) as any[]).map((opt: any, idx: number) => {
                                                        let disp = opt;
                                                        if (typeof opt !== 'string') {
                                                            disp = opt.displayValue;
                                                        }
                                                        return (
                                                            <MenuItem key={opt} value={idx}>
                                                                {disp}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Select>
                                            </FormControl>
                                        )}
                                    </Field>
                                </Grid>
                            )}
                            {options && options.length > 1 && (
                                <Grid>
                                    <FormControl variant="standard" margin={'normal'} className={classes.formControl}>
                                        <InputLabel id={'option-select-label'}>Entity</InputLabel>
                                        <Field name="optionIndex">
                                            {({ field, form, meta }: any) => (
                                                <Select
                                                    classes={{ select: classes.select }}
                                                    labelId={'option-select-label'}
                                                    variant={'standard'}
                                                    id={'option-select'}
                                                    onChange={(val) =>
                                                        form.setFieldValue(field.name, val.target.value, false)
                                                    }
                                                    value={field.value}
                                                    name={field.name}
                                                    error={meta.error}
                                                >
                                                    {options.map((opt, idx) => (
                                                        <MenuItem key={opt.label} value={idx}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        </Field>
                                    </FormControl>
                                </Grid>
                            )}
                            <Grid sx={{ ml: 'auto' }}>
                                <LrButton
                                    style={{ marginLeft: '24px', marginTop: '8px' }}
                                    type="submit"
                                    isLoading={isSubmitting}
                                >
                                    {submitText}
                                </LrButton>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </>
    );
};

export default DateRangePickerForm;
